import { useTranslate } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Tag, Form, Row, Col, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { OAuthClientOut } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import type { CrudFilter } from '@refinedev/core';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const OAuthClientList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<OAuthClientOut>({
      pagination: { pageSize: 20 },
      resource: 'iam.oauth_clients',
      onSearch,
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('iam.oauth_clients.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table {...rowNav('iam.oauth_clients', tableProps)} rowKey="id">
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="client_id"
          title={t('iam.oauth_clients.fields.client_id')}
        />

        <Table.Column
          dataIndex="client_name"
          title={t('iam.oauth_clients.fields.client_name')}
          render={(value: string) => value || '-'}
        />

        <Table.Column
          dataIndex="is_confidential"
          title={t('iam.oauth_clients.fields.is_confidential')}
          render={(value: boolean) => (
            <Tag color={value ? 'green' : 'default'}>
              {value ? t('common.yes') : t('common.no')}
            </Tag>
          )}
        />

        <Table.Column
          dataIndex="require_pkce"
          title={t('iam.oauth_clients.fields.require_pkce')}
          render={(value: boolean) => (
            <Tag color={value ? 'blue' : 'default'}>
              {value ? t('common.yes') : t('common.no')}
            </Tag>
          )}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: number) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />
      </Table>
    </List>
  );
};
